import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { Card } from '../common/Card';
import { Heading, Text } from '../common/Typography';
import { Button, Modal, ModalContent } from '../common';
import { format } from 'date-fns';
import EditSessionModal from '../sessions/EditSessionModal';
import RecordPaymentModal from '../payments/RecordPaymentModal';
import PaymentLedgerView from '../payments/PaymentLedgerView';
import PlayerPaymentDashboard from '../payments/PlayerPaymentDashboard';
import { getPlayerBalance, getPaymentLedger, getPaymentAllocations } from '../../services/paymentLedgerService';
import { exportAllPaymentData } from '../../utils/exportUtils';

const PaymentTracking = ({ clubId, currentUser, initialTab, highlightPaymentId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview'); // 'overview', 'by-player', 'by-session', 'setup', 'ledger'
  const [playerOwed, setPlayerOwed] = useState([]); // Array of { playerId, playerName, amountOwed, currency, unpaidSessionsCount, sessions: [...] }
  const [sessionsWithoutPrice, setSessionsWithoutPrice] = useState([]);
  const [sessionsWithOutstanding, setSessionsWithOutstanding] = useState([]); // Array of sessions with outstanding payment info
  const [totalOutstanding, setTotalOutstanding] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [selectedPlayerForPayment, setSelectedPlayerForPayment] = useState(null);
  const [showUnpaidSessionsModal, setShowUnpaidSessionsModal] = useState(false);
  const [selectedPlayerForUnpaidSessions, setSelectedPlayerForUnpaidSessions] = useState(null);
  const [playerBalances, setPlayerBalances] = useState({}); // Map of playerId -> balance info
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // When opening with a specific payment to highlight, switch to ledger tab
  useEffect(() => {
    if (highlightPaymentId && activeTab !== 'ledger') {
      setActiveTab('ledger');
    }
  }, [highlightPaymentId]); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount / when highlight changes

  const fetchClubData = useCallback(async () => {
    try {
      const { data: club, error: clubError } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) throw clubError;
      setSelectedClub(club);
    } catch (err) {
      console.error('Error fetching club data:', err);
    }
  }, [clubId]);

  const fetchPaymentData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch only the fields needed for payment calculations.
      //    Edit-modal fields are fetched lazily in handleEditSession.
      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select('id, name, session_date, price_per_player, currency_code, session_status')
        .eq('club_id', clubId)
        .in('session_status', ['completed', 'archived'])
        .order('session_date', { ascending: false });

      if (sessionsError) throw sessionsError;

      if (!sessions || sessions.length === 0) {
        setPlayerOwed([]);
        setSessionsWithoutPrice([]);
        setTotalOutstanding({});
        setLoading(false);
        return;
      }

      // Separate sessions with and without price_per_player
      const sessionsWithPrice = sessions.filter(s => s.price_per_player != null && s.price_per_player > 0);
      const sessionsNoPrice = sessions.filter(s => s.price_per_player == null || s.price_per_player === 0);

      setSessionsWithoutPrice(sessionsNoPrice);

      if (sessionsWithPrice.length === 0) {
        setPlayerOwed([]);
        setTotalOutstanding({});
        setLoading(false);
        return;
      }

      // 2. Fetch session_players for sessions with price
      const sessionIdsWithPrice = sessionsWithPrice.map(s => s.id);
      const { data: allSessionPlayers, error: spError } = await supabase
        .from('session_players')
        .select('id, session_id, player_id')
        .in('session_id', sessionIdsWithPrice);

      if (spError) throw spError;

      if (!allSessionPlayers || allSessionPlayers.length === 0) {
        setPlayerOwed([]);
        setTotalOutstanding({});
        setLoading(false);
        return;
      }

      // Filter to only unpaid session_players by checking payment allocations
      const { data: paidAllocations, error: allocError } = await supabase
        .from('payment_allocations')
        .select(`
          session_player_id,
          payment_ledger:payment_id!inner(is_deleted)
        `)
        .in('session_player_id', allSessionPlayers.map(sp => sp.id));

      if (allocError) {
        console.error('Error fetching payment allocations:', allocError);
        // Continue without allocation data
      }

      // Build set of session_player_ids that have active payments
      const paidSessionPlayerIds = new Set();
      (paidAllocations || []).forEach(alloc => {
        // Only count if payment is not deleted
        if (alloc.payment_ledger && !alloc.payment_ledger.is_deleted) {
          paidSessionPlayerIds.add(alloc.session_player_id);
        }
      });

      // Filter to unpaid session_players
      const sessionPlayers = allSessionPlayers.filter(sp => !paidSessionPlayerIds.has(sp.id));

      if (!sessionPlayers || sessionPlayers.length === 0) {
        setPlayerOwed([]);
        setTotalOutstanding({});
        setLoading(false);
        return;
      }

      // 3. Fetch player names and club membership status
      const playerIds = [...new Set(sessionPlayers.map(sp => sp.player_id))];
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id, first_name, last_name')
        .in('id', playerIds);

      if (playersError) throw playersError;

      // 4. Fetch player club membership status (member vs guest)
      const { data: playerClubData, error: pcError } = await supabase
        .from('players_club')
        .select('player_id, club_role')
        .eq('club_id', clubId)
        .in('player_id', playerIds);

      if (pcError) throw pcError;

      // Build map of player membership status based on club_role
      const membershipMap = {};
      (playerClubData || []).forEach(pc => {
        // Store the actual club_role, defaulting to 'guest' if not found
        membershipMap[pc.player_id] = pc.club_role || 'guest';
      });

      // Build map: playerId -> { name, owed by currency, sessions, membershipStatus }
      const playersMap = {};
      players.forEach(p => {
        playersMap[p.id] = {
          playerId: p.id,
          playerName: `${p.first_name} ${p.last_name}`,
          owedByCurrency: {},
          unpaidSessionsCount: 0,
          sessions: [],
          membershipStatus: membershipMap[p.id] || 'guest'
        };
      });

      // Build map: sessionId -> session
      const sessionsMap = {};
      sessionsWithPrice.forEach(s => { sessionsMap[s.id] = s; });

      // Aggregate unpaid amounts per player
      sessionPlayers.forEach(sp => {
        const session = sessionsMap[sp.session_id];
        if (!session) return;

        const currency = session.currency_code || 'GBP';
        const amount = parseFloat(session.price_per_player);

        if (playersMap[sp.player_id]) {
          if (!playersMap[sp.player_id].owedByCurrency[currency]) {
            playersMap[sp.player_id].owedByCurrency[currency] = 0;
          }
          playersMap[sp.player_id].owedByCurrency[currency] += amount;
          playersMap[sp.player_id].unpaidSessionsCount += 1;
          playersMap[sp.player_id].sessions.push({
            sessionId: session.id,
            sessionName: session.name,
            sessionDate: session.session_date,
            amount: amount,
            currency: currency
          });
        }
      });

      // Convert to array and sort by total owed (using first currency for sorting)
      const playersArray = Object.values(playersMap).filter(p => p.unpaidSessionsCount > 0);
      playersArray.sort((a, b) => {
        const aTotal = Object.values(a.owedByCurrency).reduce((sum, val) => sum + val, 0);
        const bTotal = Object.values(b.owedByCurrency).reduce((sum, val) => sum + val, 0);
        return bTotal - aTotal;
      });

      setPlayerOwed(playersArray);

      // Compute total outstanding by currency
      const totals = {};
      playersArray.forEach(p => {
        Object.entries(p.owedByCurrency).forEach(([currency, amount]) => {
          if (!totals[currency]) totals[currency] = 0;
          totals[currency] += amount;
        });
      });
      setTotalOutstanding(totals);

      // Build sessions with outstanding payments data
      const sessionsOutstandingMap = {};
      sessionPlayers.forEach(sp => {
        const session = sessionsMap[sp.session_id];
        if (!session) return;

        if (!sessionsOutstandingMap[sp.session_id]) {
          sessionsOutstandingMap[sp.session_id] = {
            id: session.id,
            name: session.name,
            session_date: session.session_date,
            session_status: session.session_status,
            price_per_player: session.price_per_player,
            currency_code: session.currency_code || 'GBP',
            unpaidPlayersCount: 0,
            unpaidPlayerIds: new Set(),
            outstandingAmount: 0
          };
        }
        
        // Track unique unpaid players
        sessionsOutstandingMap[sp.session_id].unpaidPlayerIds.add(sp.player_id);
        sessionsOutstandingMap[sp.session_id].unpaidPlayersCount = sessionsOutstandingMap[sp.session_id].unpaidPlayerIds.size;
        sessionsOutstandingMap[sp.session_id].outstandingAmount += parseFloat(session.price_per_player);
      });

      // Convert to array and sort by date (most recent first)
      const sessionsOutstandingArray = Object.values(sessionsOutstandingMap).map(s => ({
        ...s,
        unpaidPlayerIds: undefined // Remove Set object before storing
      }));
      sessionsOutstandingArray.sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
      
      setSessionsWithOutstanding(sessionsOutstandingArray);

    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError(err.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  // Fetch all player balances in parallel after the main data loads.
  // Kept separate so the page renders immediately and balances stream in.
  const fetchPlayerBalances = useCallback(async (playersArray) => {
    if (!playersArray || playersArray.length === 0) return;
    setLoadingBalances(true);
    try {
      const results = await Promise.all(
        playersArray.map(player => getPlayerBalance(player.playerId, clubId))
      );
      const playerBalancesMap = {};
      results.forEach((result, i) => {
        if (result.success) {
          playerBalancesMap[playersArray[i].playerId] = result;
        }
      });
      setPlayerBalances(playerBalancesMap);
    } catch (err) {
      console.error('Error fetching player balances:', err);
    } finally {
      setLoadingBalances(false);
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId) {
      fetchPaymentData();
      fetchClubData();
    }
  }, [clubId, fetchPaymentData, fetchClubData]);

  // Once playerOwed is populated, fetch balances in parallel
  useEffect(() => {
    if (playerOwed.length > 0) {
      fetchPlayerBalances(playerOwed);
    }
  }, [playerOwed, fetchPlayerBalances]);

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', CAD: 'CA$', AUD: 'A$' };
    const symbol = symbols[currency] || currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleViewSession = (sessionId) => {
    // Navigate to session wrap-up page
    navigate(`/club/${clubId}/session-wrapup/${sessionId}`);
  };

  const handleEditSession = async (sessionId) => {
    // Find the session in our existing data (sessionsWithoutPrice)
    let session = sessionsWithoutPrice.find(s => s.id === sessionId);
    
    // If not found, fetch it from the database (for sessions with outstanding payments)
    if (!session) {
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select(`
            id, 
            name, 
            session_date, 
            price_per_player, 
            currency_code, 
            session_status,
            start_time,
            end_time,
            description,
            max_players,
            number_of_courts,
            court_booking_cost,
            location,
            min_elo_level,
            session_type_id,
            signup_deadline_hours,
            waitlist_enabled,
            waitlist_promotion_type,
            signup_selection,
            is_public,
            game_type_restriction,
            enable_player_categorisation,
            session_types (
              id,
              name,
              description
            )
          `)
          .eq('id', sessionId)
          .single();
        
        if (error) throw error;
        session = data;
      } catch (error) {
        console.error('Error fetching session:', error);
        alert('Failed to load session details');
        return;
      }
    }
    
    if (session) {
      setSelectedSession(session);
      setShowEditModal(true);
    }
  };

  const handleSessionUpdated = async (sessionId, updatedData) => {
    try {
      // Update the session in the database
      const { error } = await supabase
        .from('sessions')
        .update(updatedData)
        .eq('id', sessionId);

      if (error) throw error;

      // Refresh payment data after successful update
      await fetchPaymentData();
      setShowEditModal(false);
      setSelectedSession(null);
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Failed to update session: ' + error.message);
    }
  };

  const handleRecordPayment = (player) => {
    setSelectedPlayerForPayment(player ? {
      id: player.playerId,
      first_name: player.playerName.split(' ')[0],
      last_name: player.playerName.split(' ').slice(1).join(' ')
    } : null);
    setShowRecordPaymentModal(true);
  };

  const handlePaymentRecorded = async (result) => {
    // Refresh payment data
    await fetchPaymentData();
    setShowRecordPaymentModal(false);
    setSelectedPlayerForPayment(null);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all payment ledger entries
      const ledgerResult = await getPaymentLedger(clubId, {
        includeDeleted: false
      });

      if (!ledgerResult.success) {
        throw new Error(ledgerResult.error);
      }

      const payments = ledgerResult.payments;

      // Fetch all allocations for all payments
      const allAllocationsMap = {};
      
      await Promise.all(
        payments.map(async (payment) => {
          const result = await getPaymentAllocations(payment.id);
          if (result.success) {
            allAllocationsMap[payment.id] = result.allocations;
          } else {
            allAllocationsMap[payment.id] = [];
          }
        })
      );

      // Export all data
      await exportAllPaymentData({
        payments,
        allocationsMap: allAllocationsMap,
        playerOwed,
        playerBalances,
        sessionsWithOutstanding,
        clubName: selectedClub?.name || 'club'
      });

    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export payment data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <Card className="p-8">
        <div className="text-center text-danger-dark">
          <p className="font-medium">Error loading payment data</p>
          <p className="text-sm mt-2">{error}</p>
          <Button onClick={fetchPaymentData} variant="outline" size="small" className="mt-4">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-border-medium">
        <nav className="-mb-px flex flex-wrap gap-x-4 gap-y-1">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'overview'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            📊 Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('by-player')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'by-player'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            👥 By Player
            {playerOwed.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                {playerOwed.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('by-session')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'by-session'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            📅 By Session
            {sessionsWithOutstanding.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                {sessionsWithOutstanding.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('ledger')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'ledger'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            📒 Ledger
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('player-dashboard')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'player-dashboard'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            🔍 Player
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('setup')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors text-left ${
              activeTab === 'setup'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
            }`}
          >
            ⚙️ Setup
            {sessionsWithoutPrice.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-amber-600 rounded-full">
                {sessionsWithoutPrice.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Full-page skeleton while initial data loads */}
          {loading && (
            <div className="space-y-4 animate-pulse">
              <div className="h-24 bg-surface-subtle rounded-xl" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="h-32 bg-surface-subtle rounded-xl" />
                <div className="h-32 bg-surface-subtle rounded-xl" />
                <div className="h-32 bg-surface-subtle rounded-xl" />
              </div>
            </div>
          )}

          {!loading && (
          <>
          {/* Action Button Row */}
          {playerOwed.length > 0 && (
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 w-full sm:w-auto">
              <Button 
                onClick={handleExport} 
                variant="outline"
                disabled={isExporting}
                className="w-full sm:w-auto justify-center"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export CSV
                  </>
                )}
              </Button>
              <Button onClick={() => handleRecordPayment(null)} variant="primary" className="w-full sm:w-auto justify-center">
                + Record Payment
              </Button>
            </div>
          )}

          {/* Total Outstanding */}
          {Object.keys(totalOutstanding).length > 0 && (
            <Card className="p-4 sm:p-6">
              <Heading level={3} className="mb-3">Total Outstanding</Heading>
              <div className="flex flex-wrap gap-4">
                {Object.entries(totalOutstanding).map(([currency, amount]) => (
                  <div key={currency} className="text-3xl font-bold text-text-primary">
                    {formatCurrency(amount, currency)}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Players Card */}
            <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('by-player')}>
              <div className="flex items-start justify-between">
                <div>
                  <Text className="text-sm text-text-secondary mb-1">Players with Outstanding</Text>
                  <div className="text-3xl font-bold text-text-primary mb-2">
                    {playerOwed.length}
                  </div>
                  <Button variant="outline" size="small" className="mt-2">
                    View Players →
                  </Button>
                </div>
                <div className="text-4xl">👥</div>
              </div>
            </Card>

            {/* Sessions Card */}
            <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('by-session')}>
              <div className="flex items-start justify-between">
                <div>
                  <Text className="text-sm text-text-secondary mb-1">Sessions with Outstanding</Text>
                  <div className="text-3xl font-bold text-text-primary mb-2">
                    {sessionsWithOutstanding.length}
                  </div>
                  <Button variant="outline" size="small" className="mt-2">
                    View Sessions →
                  </Button>
                </div>
                <div className="text-4xl">📅</div>
              </div>
            </Card>

            {/* Setup Card */}
            <Card 
              className={`p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer sm:col-span-2 lg:col-span-1 ${
                sessionsWithoutPrice.length > 0 ? 'border-warning bg-warning-light' : ''
              }`}
              onClick={() => setActiveTab('setup')}
            >
              <div className="flex items-start justify-between">
                <div>
                  <Text className={`text-sm mb-1 ${sessionsWithoutPrice.length > 0 ? 'text-warning-dark' : 'text-text-secondary'}`}>
                    Sessions without Price
                  </Text>
                  <div className={`text-3xl font-bold mb-2 ${sessionsWithoutPrice.length > 0 ? 'text-warning-dark' : 'text-text-primary'}`}>
                    {sessionsWithoutPrice.length}
                  </div>
                  {sessionsWithoutPrice.length > 0 ? (
                    <Button variant="warning" size="small" className="mt-2">
                      Setup Required →
                    </Button>
                  ) : (
                    <Text className="text-sm text-success-dark font-medium mt-2">✓ All set up</Text>
                  )}
                </div>
                <div className="text-4xl">{sessionsWithoutPrice.length > 0 ? '⚠️' : '✅'}</div>
              </div>
            </Card>
          </div>

          {/* Empty state */}
          {playerOwed.length === 0 && sessionsWithOutstanding.length === 0 && sessionsWithoutPrice.length === 0 && (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <Heading level={3} className="mb-2">All caught up!</Heading>
              <Text className="text-text-secondary">
                No outstanding payments to track. Great job!
              </Text>
            </Card>
          )}

          {/* No outstanding but needs setup */}
          {playerOwed.length === 0 && sessionsWithOutstanding.length === 0 && sessionsWithoutPrice.length > 0 && (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">💳</div>
              <Heading level={3} className="mb-2">No outstanding payments</Heading>
              <Text className="text-text-secondary mb-4">
                All players have been marked as paid, but you have {sessionsWithoutPrice.length} session{sessionsWithoutPrice.length !== 1 ? 's' : ''} without pricing set.
              </Text>
              <Button onClick={() => setActiveTab('setup')} variant="primary">
                Set Up Session Pricing
              </Button>
            </Card>
          )}
          </>
          )}
        </div>
      )}

      {/* By Player Tab */}
      {activeTab === 'by-player' && (
        <div className="space-y-6">
          {/* Skeleton while loading */}
          {loading && (
            <Card>
              <div className="p-6 border-b border-border-medium animate-pulse">
                <div className="h-5 bg-surface-subtle rounded w-64 mb-2" />
                <div className="h-4 bg-surface-subtle rounded w-40" />
              </div>
            </Card>
          )}
          {/* Action Button Row */}
          {!loading && playerOwed.length > 0 && (
            <div className="flex justify-stretch sm:justify-end">
              <Button onClick={() => handleRecordPayment(null)} variant="primary" className="w-full sm:w-auto justify-center">
                + Record Payment
              </Button>
            </div>
          )}

          {!loading && playerOwed.length > 0 ? (
            <Card>
              <div className="p-4 sm:p-6 border-b border-border-medium">
                <Heading level={3}>Players with Outstanding Payments</Heading>
<Text className="text-sm text-text-secondary mt-1">
                {playerOwed.length} player{playerOwed.length !== 1 ? 's' : ''} with unpaid sessions
                </Text>
              </div>
              <div className="lg:hidden divide-y divide-border-medium">
                {playerOwed.map((player) => {
                  const balance = playerBalances[player.playerId];
                  const badgeClass =
                    player.membershipStatus === 'owner' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' :
                    player.membershipStatus === 'admin' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' :
                    player.membershipStatus === 'host' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' :
                    player.membershipStatus === 'member' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' :
                    'bg-surface-subtle text-text-secondary';
                  const badgeLabel =
                    player.membershipStatus === 'owner' ? 'Owner' :
                    player.membershipStatus === 'admin' ? 'Admin' :
                    player.membershipStatus === 'host' ? 'Host' :
                    player.membershipStatus === 'member' ? 'Member' :
                    'Guest';
                  return (
                    <div key={player.playerId} className="p-4 space-y-3">
                      <div className="font-medium text-text-primary">{player.playerName}</div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
                        {badgeLabel}
                      </span>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-text-muted">Amount owed</div>
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            {Object.entries(player.owedByCurrency).map(([currency, amount]) => (
                              <Text key={currency} className="font-semibold text-text-primary">
                                {formatCurrency(amount, currency)}
                              </Text>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-text-muted">Unpaid sessions</div>
                          <Text className="font-medium text-text-primary mt-0.5">{player.unpaidSessionsCount}</Text>
                        </div>
                        <div>
                          <div className="text-xs text-text-muted">Credit balance</div>
                          {loadingBalances && !balance ? (
                            <div className="h-4 w-16 bg-surface-subtle rounded animate-pulse mt-1" />
                          ) : balance ? (
                            <Text className={`font-medium mt-0.5 ${balance.credit > 0 ? 'text-success-dark' : 'text-text-secondary'}`}>
                              {formatCurrency(balance.credit, balance.currencyCode)}
                            </Text>
                          ) : <Text className="text-text-muted mt-0.5">—</Text>}
                        </div>
                        <div>
                          <div className="text-xs text-text-muted">Net balance</div>
                          {loadingBalances && !balance ? (
                            <div className="h-4 w-16 bg-surface-subtle rounded animate-pulse mt-1" />
                          ) : balance ? (
                            <Text className={`font-semibold mt-0.5 ${balance.netBalance >= 0 ? 'text-success-dark' : 'text-danger-dark'}`}>
                              {formatCurrency(balance.netBalance, balance.currencyCode)}
                            </Text>
                          ) : <Text className="text-text-muted mt-0.5">—</Text>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 pt-1">
                        <Button
                          onClick={() => handleRecordPayment(player)}
                          variant="primary"
                          size="small"
                          className="w-full justify-center"
                        >
                          Record Payment
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedPlayerForUnpaidSessions(player);
                            setShowUnpaidSessionsModal(true);
                          }}
                          variant="outline"
                          size="small"
                          className="w-full justify-center"
                        >
                          View Sessions
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-subtle border-b border-border-medium">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Amount Owed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Credit Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Net Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Unpaid Sessions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-medium">
                    {loading && (
                      // Skeleton rows while main data loads
                      [1,2,3,4,5].map(i => (
                        <tr key={i} className="animate-pulse">
                          {[1,2,3,4,5,6,7].map(j => (
                            <td key={j} className="px-6 py-4">
                              <div className="h-4 bg-slate-200 rounded w-24" />
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                    {!loading && playerOwed.map(player => {
                      const balance = playerBalances[player.playerId];
                      return (
                        <tr key={player.playerId} className="hover:bg-surface-subtle">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Text className="font-medium text-text-primary">{player.playerName}</Text>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              player.membershipStatus === 'owner' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' :
                              player.membershipStatus === 'admin' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' :
                              player.membershipStatus === 'host' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' :
                              player.membershipStatus === 'member' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' :
                              'bg-surface-subtle text-text-secondary'
                            }`}>
                              {player.membershipStatus === 'owner' ? 'Owner' :
                               player.membershipStatus === 'admin' ? 'Admin' :
                               player.membershipStatus === 'host' ? 'Host' :
                               player.membershipStatus === 'member' ? 'Member' :
                               'Guest'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {Object.entries(player.owedByCurrency).map(([currency, amount]) => (
                                <Text key={currency} className="font-semibold text-text-primary">
                                  {formatCurrency(amount, currency)}
                                </Text>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {loadingBalances && !balance ? (
                              <div className="h-4 w-16 bg-surface-subtle rounded animate-pulse" />
                            ) : balance ? (
                              <Text className={`font-medium ${balance.credit > 0 ? 'text-success-dark' : 'text-text-secondary'}`}>
                                {formatCurrency(balance.credit, balance.currencyCode)}
                              </Text>
                            ) : <Text className="text-text-muted">—</Text>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {loadingBalances && !balance ? (
                              <div className="h-4 w-16 bg-surface-subtle rounded animate-pulse" />
                            ) : balance ? (
                              <Text className={`font-semibold ${balance.netBalance >= 0 ? 'text-success-dark' : 'text-danger-dark'}`}>
                                {formatCurrency(balance.netBalance, balance.currencyCode)}
                              </Text>
                            ) : <Text className="text-text-muted">—</Text>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Text className="text-text-secondary">{player.unpaidSessionsCount}</Text>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap space-x-2">
                            <Button
                              onClick={() => handleRecordPayment(player)}
                              variant="primary"
                              size="small"
                            >
                              Record Payment
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedPlayerForUnpaidSessions(player);
                                setShowUnpaidSessionsModal(true);
                              }}
                              variant="outline"
                              size="small"
                            >
                              View Sessions
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <Heading level={3} className="mb-2">No outstanding player payments</Heading>
              <Text className="text-text-secondary">
                All players with session prices set have been marked as paid.
              </Text>
            </Card>
          )}
        </div>
      )}

      {/* By Session Tab */}
      {activeTab === 'by-session' && (
        <div className="space-y-6">
          {sessionsWithOutstanding.length > 0 ? (
            <Card>
              <div className="p-4 sm:p-6 border-b border-border-medium">
                <Heading level={3}>Sessions with Outstanding Payments</Heading>
                <Text className="text-sm text-text-secondary mt-1">
                  {sessionsWithOutstanding.length} session{sessionsWithOutstanding.length !== 1 ? 's' : ''} with unpaid players
                </Text>
              </div>
              <div className="lg:hidden divide-y divide-border-medium">
                {sessionsWithOutstanding.map((session) => (
                  <div key={session.id} className="p-4 space-y-2">
                    <div className="font-medium text-text-primary break-words">{session.name}</div>
                    <div className="text-sm text-text-secondary">{formatDate(session.session_date)}</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      session.session_status === 'completed'
                        ? 'bg-warning-light text-warning-dark'
                        : 'bg-surface-subtle text-text-secondary'
                    }`}>
                      {session.session_status}
                    </span>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <span className="text-text-secondary">Unpaid: <strong className="text-text-primary">{session.unpaidPlayersCount}</strong></span>
                      <span className="text-text-secondary">Outstanding: <strong className="text-text-primary">{formatCurrency(session.outstandingAmount, session.currency_code)}</strong></span>
                    </div>
                    <Button
                      onClick={() => handleViewSession(session.id)}
                      variant="outline"
                      size="small"
                      className="w-full justify-center"
                    >
                      View Session
                    </Button>
                  </div>
                ))}
              </div>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-subtle border-b border-border-medium">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Session
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Unpaid Players
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Total Outstanding
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-medium">
                    {sessionsWithOutstanding.map(session => (
                      <tr key={session.id} className="hover:bg-surface-subtle">
                        <td className="px-6 py-4">
                          <Text className="font-medium text-text-primary">{session.name}</Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Text className="text-text-secondary">{formatDate(session.session_date)}</Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            session.session_status === 'completed' 
                              ? 'bg-warning-light text-warning-dark' 
                              : 'bg-surface-subtle text-text-secondary'
                          }`}>
                            {session.session_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Text className="font-semibold text-text-primary">{session.unpaidPlayersCount}</Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Text className="font-semibold text-text-primary">
                            {formatCurrency(session.outstandingAmount, session.currency_code)}
                          </Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Button
                            onClick={() => handleViewSession(session.id)}
                            variant="outline"
                            size="small"
                          >
                            View Session
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <Heading level={3} className="mb-2">No sessions with outstanding payments</Heading>
              <Text className="text-text-secondary">
                All sessions with prices set have been fully reconciled.
              </Text>
            </Card>
          )}
        </div>
      )}

      {/* Ledger Tab */}
      {activeTab === 'ledger' && (
        <PaymentLedgerView 
          clubId={clubId} 
          clubName={selectedClub?.name}
          playerOwed={playerOwed}
          playerBalances={playerBalances}
          sessionsWithOutstanding={sessionsWithOutstanding}
          highlightPaymentId={highlightPaymentId}
        />
      )}

      {/* Player Dashboard Tab */}
      {activeTab === 'player-dashboard' && (
        <PlayerPaymentDashboard
          clubId={clubId}
          currentUser={currentUser}
          onRecordPayment={(player) => {
            setSelectedPlayerForPayment(player);
            setShowRecordPaymentModal(true);
          }}
        />
      )}

      {/* Setup Tab */}
      {activeTab === 'setup' && (
        <div className="space-y-6">
          {sessionsWithoutPrice.length > 0 ? (
            <>
              {/* Warning Banner */}
              <Card className="p-4 bg-warning-light border border-warning dark:bg-warning/20 dark:border-warning">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div className="flex-1">
                    <Text className="font-medium text-warning-dark mb-1">
                      {sessionsWithoutPrice.length} session{sessionsWithoutPrice.length !== 1 ? 's' : ''} without a price per player
                    </Text>
                    <Text className="text-sm text-warning-dark">
                      Sessions without a price per player set don't contribute to payment tracking. Add pricing to include them in outstanding amounts.
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Sessions Table */}
              <Card>
                <div className="p-4 sm:p-6 border-b border-border-medium">
                  <Heading level={3}>Sessions Requiring Price Setup</Heading>
                  <Text className="text-sm text-text-secondary mt-1">
                    Add a price per player to these sessions to enable payment tracking
                  </Text>
                </div>
                <div className="lg:hidden divide-y divide-border-medium">
                  {sessionsWithoutPrice.map((session) => (
                    <div key={session.id} className="p-4 space-y-3">
                      <div className="font-medium text-text-primary break-words">{session.name}</div>
                      <div className="text-sm text-text-secondary">{formatDate(session.session_date)}</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        session.session_status === 'completed'
                          ? 'bg-warning-light text-warning-dark'
                          : 'bg-surface-subtle text-text-secondary'
                      }`}>
                        {session.session_status}
                      </span>
                      <Button
                        onClick={() => handleEditSession(session.id)}
                        variant="outline"
                        size="small"
                        className="w-full justify-center"
                      >
                        Add Price
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-subtle border-b border-border-medium">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Session
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-medium">
                      {sessionsWithoutPrice.map(session => (
                        <tr key={session.id} className="hover:bg-surface-subtle">
                          <td className="px-6 py-4">
                            <Text className="font-medium text-text-primary">{session.name}</Text>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Text className="text-text-secondary">{formatDate(session.session_date)}</Text>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              session.session_status === 'completed' 
                                ? 'bg-warning-light text-warning-dark' 
                                : 'bg-surface-subtle text-text-secondary'
                            }`}>
                              {session.session_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Button
                              onClick={() => handleEditSession(session.id)}
                              variant="outline"
                              size="small"
                            >
                              Add Price
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <Heading level={3} className="mb-2">All sessions have pricing set</Heading>
              <Text className="text-text-secondary">
                Great! All your completed and archived sessions have a price per player configured.
              </Text>
            </Card>
          )}
        </div>
      )}

      {/* Edit Session Modal */}
      {showEditModal && selectedSession && selectedClub && (
        <EditSessionModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSession(null);
          }}
          session={selectedSession}
          selectedClub={selectedClub}
          currentUser={currentUser}
          onSessionUpdated={handleSessionUpdated}
        />
      )}

      {/* Record Payment Modal */}
      {showRecordPaymentModal && (
        <RecordPaymentModal
          isOpen={showRecordPaymentModal}
          onClose={() => {
            setShowRecordPaymentModal(false);
            setSelectedPlayerForPayment(null);
          }}
          clubId={clubId}
          onPaymentRecorded={handlePaymentRecorded}
          preselectedPlayer={selectedPlayerForPayment}
        />
      )}

      {/* Unpaid sessions list modal (By Player tab) */}
      {showUnpaidSessionsModal && selectedPlayerForUnpaidSessions && (
        <Modal
          isOpen={showUnpaidSessionsModal}
          onClose={() => {
            setShowUnpaidSessionsModal(false);
            setSelectedPlayerForUnpaidSessions(null);
          }}
        >
          <ModalContent size="medium">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
              <Heading level={3} className="mb-0 break-words pr-2">
                Unpaid sessions — {selectedPlayerForUnpaidSessions.playerName}
              </Heading>
              <button
                type="button"
                onClick={() => {
                  setShowUnpaidSessionsModal(false);
                  setSelectedPlayerForUnpaidSessions(null);
                }}
                className="text-text-muted hover:text-text-secondary p-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <Text className="text-sm text-text-secondary mb-4">
              {selectedPlayerForUnpaidSessions.unpaidSessionsCount} session{selectedPlayerForUnpaidSessions.unpaidSessionsCount !== 1 ? 's' : ''} not yet paid
            </Text>
            <div className="max-h-80 overflow-y-auto border border-border-medium rounded-lg divide-y divide-border-medium">
              {selectedPlayerForUnpaidSessions.sessions
                .sort((a, b) => new Date(a.sessionDate) - new Date(b.sessionDate))
                .map((session) => (
                  <div
                    key={session.sessionId}
                    className="p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between hover:bg-surface-subtle"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-text-primary">{session.sessionName || 'Session'}</div>
                      <div className="text-xs text-text-muted mt-0.5">{formatDate(session.sessionDate)}</div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 shrink-0">
                      <span className="text-sm font-semibold text-text-primary sm:text-right sm:min-w-[5rem]">
                        {formatCurrency(session.amount, session.currency)}
                      </span>
                      <Button
                        variant="outline"
                        size="small"
                        className="w-full sm:w-auto justify-center"
                        onClick={() => {
                          setShowUnpaidSessionsModal(false);
                          setSelectedPlayerForUnpaidSessions(null);
                          handleViewSession(session.sessionId);
                        }}
                      >
                        Go to wrap-up
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  setShowUnpaidSessionsModal(false);
                  setSelectedPlayerForUnpaidSessions(null);
                  handleRecordPayment(selectedPlayerForUnpaidSessions);
                }}
              >
                Record Payment
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default PaymentTracking;
